/**
 * Typed corpus loaders and the font loader — §3.1, L1, L5.
 *
 * L1 IS ENFORCED STRUCTURALLY HERE: `loadPageWithFonts` loads the page JSON AND its
 * fonts in one promise, so page data is simply never available before the font that
 * renders it. QCF4 encodes each WORD as one glyph in the Unicode Private Use Area;
 * those codepoints are meaningless in any other typeface, so a fallback face renders
 * a screen of tofu.
 */
import { CORPUS_BASE } from './constants'
import type { Meta, MushafPage, ReciterAudio, SearchRow, TafsirPage } from './types'

const pad = (page: number) => String(page).padStart(3, '0')

export const corpusUrls = {
  meta: () => `${CORPUS_BASE}/meta.json`,
  search: () => `${CORPUS_BASE}/search.json`,
  page: (page: number) => `${CORPUS_BASE}/mushaf/${pad(page)}.json`,
  tafsir: (page: number) => `${CORPUS_BASE}/tafsir/${pad(page)}.json`,
  audio: (reciterId: string) => `${CORPUS_BASE}/audio/${reciterId}.json`,
  /**
   * The family name in the page data is NOT the filename.
   *
   * The 47 page faces are named `QCF4_Hafs_NN` in the JSON but ship as
   * `QCF4_Hafs_NN_W.woff2`; the bismillah face `QCF4_QBSML` ships under its own name
   * with no suffix. Getting this wrong 404s every page font silently, the browser
   * falls back to a face that has no PUA glyphs, and the whole page is tofu (L1) —
   * while the layout measurement happily converges on the WRONG advance widths.
   */
  pageFont: (font: string) =>
    `${CORPUS_BASE}/fonts/${font.startsWith('QCF4_Hafs_') ? `${font}_W` : font}.woff2`,
  v4Font: (page: number, ext: 'woff2' | 'ttf') => `${CORPUS_BASE}/fonts/v4tajweed/p${page}.${ext}`,
  /**
   * Janna LT ships TWO weights only — Regular and Bold. The UI declares 600 as well,
   * so 600 and 700 resolve to the SAME Bold file rather than shipping a duplicate or
   * letting the browser synthesise a semibold (synthetic bold smears Arabic joins).
   * Keep this in step with the three `@font-face` blocks in `app.css`.
   */
  uiFont: (weight: 400 | 600 | 700) =>
    `${CORPUS_BASE}/fonts/ui/janna-lt-${weight === 400 ? 400 : 700}.woff2`,
} as const

async function getJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`${url} → ${response.status}`)
  return (await response.json()) as T
}

export const loadMeta = (signal?: AbortSignal) => getJson<Meta>(corpusUrls.meta(), signal)
export const loadSearchIndex = (signal?: AbortSignal) =>
  getJson<SearchRow[]>(corpusUrls.search(), signal)
export const loadTafsirPage = (page: number, signal?: AbortSignal) =>
  getJson<TafsirPage>(corpusUrls.tafsir(page), signal)
export const loadReciterAudio = (id: string, signal?: AbortSignal) =>
  getJson<ReciterAudio>(corpusUrls.audio(id), signal)

// ---------------------------------------------------------------------------
// Fonts
// ---------------------------------------------------------------------------

const loadedFaces = new Set<string>()

/**
 * `font-display` must be BLOCK, not swap — there is no acceptable fallback to swap
 * from. A swap would paint a screen of tofu and then replace it.
 */
async function addFace(family: string, url: string, descriptors: FontFaceDescriptors = {}) {
  const key = `${family}|${url}`
  if (loadedFaces.has(key)) return
  const face = new FontFace(family, `url(${JSON.stringify(url)})`, {
    display: 'block',
    ...descriptors,
  })
  await face.load()
  document.fonts.add(face)
  loadedFaces.add(key)
}

/**
 * L5: ALL 604 v4 TAJWEED COLOUR FONTS REGISTER UNDER ONE FAMILY NAME.
 *
 * `@font-palette-values` is declared per family, and 604 families would need 604
 * duplicate palette declarations. Only one page is on screen at a time, so remove the
 * previous face before adding the next.
 */
export const V4_FAMILY = 'V4TJ'

let currentV4: FontFace | null = null

export async function loadV4Font(page: number): Promise<boolean> {
  if (currentV4) {
    document.fonts.delete(currentV4)
    currentV4 = null
  }

  // PAGE 328 EXISTS ONLY AS .ttf UPSTREAM — fall back from .woff2 to .ttf, or one
  // page of the mushaf is unrenderable (T6).
  for (const ext of ['woff2', 'ttf'] as const) {
    try {
      const face = new FontFace(V4_FAMILY, `url(${JSON.stringify(corpusUrls.v4Font(page, ext))})`, {
        display: 'block',
      })
      await face.load()
      document.fonts.add(face)
      currentV4 = face
      return true
    } catch {
      // Try the next extension.
    }
  }
  // Loading the v4 font must be failure-tolerant: fall through to the monochrome
  // glyphs rather than blanking the page (§7.7).
  return false
}

export interface LoadedPage {
  data: MushafPage
  /** Whether the v4 colour font for this page is available. */
  v4Ready: boolean
}

/**
 * Load the page JSON AND its fonts together. This is L1, enforced structurally rather
 * than by discipline: there is no code path that hands a component page data without
 * the font it needs.
 *
 * `withV4` is passed rather than read from settings so this stays a pure-ish loader.
 */
export async function loadPageWithFonts(
  page: number,
  options: { meta: Meta; withV4: boolean; signal?: AbortSignal },
): Promise<LoadedPage> {
  const { meta, withV4, signal } = options
  const data = await getJson<MushafPage>(corpusUrls.page(page), signal)

  const families = new Set<string>([data.font])
  for (const line of data.lines) {
    for (const word of line.words) families.add(word.font)
  }
  families.add(meta.bismillahFont)

  await Promise.all(
    [...families].map((family) =>
      addFace(family, corpusUrls.pageFont(family)).catch(() => {
        // A missing page face is fatal to rendering, but throwing here would take the
        // whole page down; the measurement pass simply never converges instead.
        console.error(`failed to load page font ${family}`)
      }),
    ),
  )

  const v4Ready = withV4 ? await loadV4Font(page) : false
  return { data, v4Ready }
}
